import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, BookOpen, AlertTriangle, Sparkles, CheckCircle2, X } from 'lucide-react';

/**
 * AiOmnibarPanel — absolute-positioned overlay rendered directly below
 * the TopBar search input. The shape of the panel is driven entirely
 * by the `state` prop, so the TopBar only owns the controller state
 * and this component focuses on presentation.
 *
 * States:
 *   'busy'        — "Processing via Gemini AI…" loading shimmer.
 *   'knowledge'   — top-N markdown articles (knowledge-lookup result).
 *   'ambiguous'   — disambiguation dropdown listing candidate entities.
 *   'success'     — green confirmation card (briefly visible before
 *                   the parent redirects to the invoice page).
 *   'error'       — red banner with a retry hint.
 *   null          — render nothing.
 *
 * Mobile (<768px): the panel spans `left-0 right-0` of its positioned
 * parent (which the TopBar makes full-viewport-width on mobile via the
 * media-query overrides). 44px tap targets on every action.
 */
export function AiOmnibarPanel({
  state,
  loadingLabel = 'Processing via Gemini AI…',
  knowledge = [],
  ambiguousField,
  candidates = [],
  successMessage,
  errorMessage,
  onClose,
  onCandidatePick,
}) {
  if (!state) return null;

  return (
    <div
      data-testid="ai-omnibar-panel"
      className="absolute top-full mt-2 left-0 right-0 z-[60] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto"
      role="dialog"
      aria-live="polite"
    >
      {/* Header strip — same across every state so the panel feels
          cohesive; close button always present on touch devices. */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-700/60"
        style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.07) 0%, transparent 100%)' }}
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
          <span>AI Omnibar</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close AI panel"
          className="w-8 h-8 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200"
          data-testid="ai-omnibar-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {state === 'busy' && (
        <div
          className="flex items-center gap-3 px-4 py-5 text-sm text-slate-600 dark:text-slate-300"
          data-testid="ai-omnibar-busy"
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#f59e0b' }} />
          <span>{loadingLabel}</span>
        </div>
      )}

      {state === 'knowledge' && (
        <div data-testid="ai-omnibar-knowledge" className="divide-y divide-slate-100 dark:divide-slate-700/60">
          <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <BookOpen className="w-3 h-3" /> Help articles
          </div>
          {knowledge.length === 0 && (
            <div className="px-4 py-5 text-sm text-slate-500">
              No matching articles. Try a different query — e.g. "how do I create an invoice".
            </div>
          )}
          {knowledge.map((art) => (
            <article
              key={art.id}
              data-testid={`ai-omnibar-article-${art.id}`}
              className="px-4 py-3"
            >
              <header className="flex items-baseline justify-between gap-3 mb-1">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                  {art.title}
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 flex-shrink-0">
                  {art.category}
                </span>
              </header>
              <div className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed">
                <ReactMarkdown>{art.content_markdown}</ReactMarkdown>
              </div>
            </article>
          ))}
        </div>
      )}

      {state === 'ambiguous' && (
        <div data-testid="ai-omnibar-ambiguous">
          <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" style={{ color: '#f59e0b' }} />
            {ambiguousField === 'customer'
              ? (candidates.length ? 'Pick the customer you meant' : 'No matching customer found')
              : ambiguousField === 'vendor'
                ? (candidates.length ? 'Pick the vendor you meant' : 'No matching vendor found')
                : (candidates.length ? 'Pick the product you meant' : 'No matching product found')}
          </div>
          {candidates.length === 0 && (
            <div className="px-4 py-5 text-sm text-slate-500">
              {ambiguousField === 'product:0' || ambiguousField?.startsWith?.('product:')
                ? 'No close matches for that product. Add the product to your catalog first, then retry.'
                : ambiguousField === 'vendor'
                  ? 'No close matches for that vendor. Add the vendor first, then retry.'
                  : 'No close matches for that customer. Add the customer first, then retry.'}
            </div>
          )}
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onCandidatePick?.(c)}
              data-testid={`ai-omnibar-candidate-${c.id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 min-h-[44px] text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 active:bg-slate-100 dark:active:bg-slate-700/60 transition-colors border-t border-slate-100 dark:border-slate-700/60"
            >
              <span className="font-medium truncate">{c.name}</span>
              {c.extra && (
                <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">{c.extra}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {state === 'success' && (
        <div
          className="flex items-center gap-3 px-4 py-5 text-sm text-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/20 dark:text-emerald-300"
          data-testid="ai-omnibar-success"
        >
          <CheckCircle2 className="w-4 h-4" />
          <span>{successMessage}</span>
        </div>
      )}

      {state === 'error' && (
        <div
          className="flex items-start gap-3 px-4 py-4 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300"
          data-testid="ai-omnibar-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMessage || 'Something went wrong — please retry.'}</span>
        </div>
      )}
    </div>
  );
}

export default AiOmnibarPanel;
